import { STUB_FN_FALSE, STUB_FN_NULL } from 'projects/exi-ui/src/lib/utils/stubs';
import { BehaviorSubject } from 'rxjs';
import { Ring } from './chain.class';
import { Checkbox } from '../../components/checkbox/checkbox.component';
import { Casher } from './cash.class';
import { AbstractTable } from './context/abstract/table.class';


export type SelectionMode = 'single' | 'multi' | 'none';

export interface SelectionArgs<D extends object> {
   mode: SelectionMode;
   toggle: (row: D) => void;
   onChange: (val: D, selected: D[], others: D[]) => void;
   isSelected: (row: D) => boolean;
   isDisabled?: (row: D) => boolean;
}


export const stubSelection: SelectionArgs<any> = {
   mode: 'none',
   toggle: STUB_FN_NULL,
   onChange: STUB_FN_NULL,
   isSelected: STUB_FN_FALSE,
   isDisabled: STUB_FN_FALSE,
};


export class Selection<D extends object> extends Casher<D[]> implements Checkbox<D>, Ring<D[]> {
   public input = new BehaviorSubject<D[]>([]);
   public output = new BehaviorSubject<D[]>([]);
   readonly mode: SelectionMode;
   readonly isSelected: (val: D) => boolean;
   readonly toggle: (val: D) => void;
   readonly select: (val: D) => void;
   readonly unselect: (val: D) => void;
   readonly isDisabled?: (val: D) => boolean;
   public get isSomeSelected(): boolean {
      return this.input.getValue().some(row => this.isSelected(row));
   }


   constructor(
      private ctx: AbstractTable<D>, 
      { mode, toggle, onChange, isSelected, isDisabled }: SelectionArgs<D> 
   ) {
      super({
         cellId: `Selection`,
         containerId: ctx.id,
         isActive: ctx.isCashable
      });

      this.mode = mode;
      this.isSelected = isSelected;
      this.isDisabled = isDisabled || STUB_FN_FALSE;
      this.toggle = toggle;
      this.select = val => onChange(val, ...this.getSelected());
      this.unselect = val => onChange(val, ...this.getSelected());
      // this.apply(); -- Will not work because data cell emmits empty array as first value, which will overlap cahsed values
   }


   public subscribe() {
      return this.input.subscribe(rows => {
         const lastSelection = this.output.getValue()
            .filter(row => this.isSelected(row))
            .map(row => JSON.stringify(row));

         for (const row of rows) {
            const isInLastSelection = lastSelection.find(selected => selected === JSON.stringify(row));

            if (isInLastSelection) {
               this.toggle(row);
            }
         }

         this.output.next(rows);
      });
   }


   public unselectAll() {
      this.input.getValue().forEach(row => {
         if (this.isSelected(row)) {
            this.toggle(row);
         }
      });
   }


   public selectAll() {
      this.input.getValue().forEach(row => {
         if (!this.isDisabled(row) && !this.isSelected(row)) {
            this.toggle(row);
         }
      });
   }


   public getSelected(): [ D[], D[] ] {
      const { isSelected, isDisabled } = this;
      const selected: D[] = [];
      const others: D[] = [];

      for (const data of this.input.getValue()) {
         if (isSelected(data) && !isDisabled(data)) {
            selected.push(data);
         } else {
            others.push(data);
         }
      }

      return [ selected,  others ];
   }


   public save() {
      return this.output.getValue()
         .filter(row => this.isSelected(row))
         .map(row => { 
            this.toggle(row);
            return row;
         });
   }


   public restore(rows: D[]) {
      this.output.next(rows);
   }
}


